import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { schemaAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Table, Column } from '../types';
import { 
  Table as TableIcon, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Database,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Settings,
  EyeOff,
  DollarSign,
  SortAsc
} from 'lucide-react';

const dataTypes = [
  'int',
  'bigint',
  'varchar',
  'text',
  'decimal',
  'datetime',
  'date',
  'boolean',
  'json'
];

const SchemaEditorPage: React.FC = () => {
  const { subdomain } = useAuth();
  const queryClient = useQueryClient();
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [editingColumn, setEditingColumn] = useState<Column | null>(null);
  const [editingColumnIndex, setEditingColumnIndex] = useState<number | null>(null);
  const [isAddingTable, setIsAddingTable] = useState(false);
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: schema, isLoading } = useQuery(
    'schema',
    schemaAPI.getSchema,
    {
      enabled: !!subdomain,
      retry: false,
    }
  );

  const createTableMutation = useMutation(schemaAPI.createTable, {
    onSuccess: () => {
      queryClient.invalidateQueries('schema');
      setIsAddingTable(false);
      setEditingTable(null);
      setMessage({ type: 'success', text: 'Table created successfully!' });
    },
    onError: (error: any) => {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to create table' });
    }
  });

  const updateTableMutation = useMutation(
    ({ tableName, table }: { tableName: string; table: Table }) => 
      schemaAPI.updateTable(tableName, table),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schema');
        setEditingTable(null);
        setMessage({ type: 'success', text: 'Table updated successfully!' });
      },
      onError: (error: any) => {
        setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update table' });
      }
    }
  );

  const deleteTableMutation = useMutation(schemaAPI.deleteTable, {
    onSuccess: () => {
      queryClient.invalidateQueries('schema');
      setSelectedTable(null);
      setMessage({ type: 'success', text: 'Table deleted successfully!' });
    },
    onError: (error: any) => {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete table' });
    }
  });

  const deleteColumnMutation = useMutation(
    ({ tableName, columnName }: { tableName: string; columnName: string }) =>
      schemaAPI.deleteColumn(tableName, columnName),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schema');
        setMessage({ type: 'success', text: 'Column deleted successfully!' });
      },
      onError: (error: any) => {
        setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete column' });
      }
    }
  );

  const selectedTableData = schema?.tables.find(t => t.name === selectedTable);

  const validateTable = (table: Table): string | null => {
    if (!table.name || !table.name.match(/^[a-zA-Z][a-zA-Z0-9_]*$/)) {
      return 'Table name must start with a letter and contain only letters, numbers, and underscores';
    }
    if (!table.description || table.description.trim().length === 0) {
      return 'Table description is required';
    }
    if (table.columns.length === 0) {
      return 'Table must have at least one column';
    }
    
    const hasPrimaryKey = table.columns.some(col => col.isPrimaryKey);
    if (!hasPrimaryKey) {
      return 'Table must have at least one primary key column';
    }
    
    const columnNames = table.columns.map(col => col.name);
    const uniqueNames = new Set(columnNames);
    if (columnNames.length !== uniqueNames.size) {
      return 'Column names must be unique';
    }
    
    for (const column of table.columns) {
      if (!column.name || !column.name.match(/^[a-zA-Z][a-zA-Z0-9_]*$/)) {
        return `Column "${column.name}" has invalid name format`;
      }
      if (!column.description || column.description.trim().length === 0) {
        return `Column "${column.name}" must have a description`;
      }
    }
    
    return null;
  };

  const handleCreateTable = () => {
    if (editingTable) {
      const error = validateTable(editingTable);
      if (error) {
        setMessage({ type: 'error', text: error });
        return;
      }
      createTableMutation.mutate(editingTable);
    }
  };

  const handleUpdateTable = () => {
    if (editingTable && selectedTable) {
      const error = validateTable(editingTable);
      if (error) {
        setMessage({ type: 'error', text: error });
        return;
      }
      updateTableMutation.mutate({ tableName: selectedTable, table: editingTable });
    }
  };

  const handleDeleteTable = (tableName: string) => {
    if (window.confirm(`Are you sure you want to delete the table "${tableName}"? This action cannot be undone.`)) {
      deleteTableMutation.mutate(tableName);
    }
  };

  const handleDeleteColumn = (tableName: string, columnName: string) => {
    if (window.confirm(`Are you sure you want to delete the column "${columnName}"? This action cannot be undone.`)) {
      deleteColumnMutation.mutate({ tableName, columnName });
    }
  };

  const validateColumn = (column: Column): string | null => {
    if (!column.name || !column.name.match(/^[a-zA-Z][a-zA-Z0-9_]*$/)) {
      return 'Column name must start with a letter and contain only letters, numbers, and underscores';
    }
    if (!column.description || column.description.trim().length === 0) {
      return 'Column description is required';
    }
    if (column.isForeignKey && !column.foreignKeyReference) {
      return 'Foreign key columns must specify a reference';
    }
    return null;
  };

  const handleAddColumn = () => {
    if (editingTable && editingColumn) {
      const error = validateColumn(editingColumn);
      if (error) {
        setMessage({ type: 'error', text: error });
        return;
      }
      
      const updatedTable = {
        ...editingTable,
        columns: [...editingTable.columns, editingColumn]
      };
      setEditingTable(updatedTable);
      setEditingColumn(null);
      setIsAddingColumn(false);
    }
  };

  const handleUpdateColumn = () => {
    if (editingTable && editingColumn && editingColumnIndex !== null) {
      const error = validateColumn(editingColumn);
      if (error) {
        setMessage({ type: 'error', text: error });
        return;
      }
      
      const updatedColumns = [...editingTable.columns];
      updatedColumns[editingColumnIndex] = editingColumn;
      setEditingTable({
        ...editingTable,
        columns: updatedColumns
      });
      setEditingColumn(null);
      setEditingColumnIndex(null);
    }
  };

  const handleRemoveColumn = (index: number) => {
    if (editingTable) {
      const updatedColumns = editingTable.columns.filter((_, i) => i !== index);
      setEditingTable({
        ...editingTable,
        columns: updatedColumns
      });
    }
  };


  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (!subdomain) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please configure your subdomain in settings to manage the schema.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return <div className="p-6">Loading schema...</div>;
  }

  return (
    <div className="h-full flex">
      {/* Tables Sidebar */}
      <div className="w-80 border-r bg-muted/10 p-4 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Database className="h-5 w-5" />
            Tables
          </h2>
          <Button
            size="sm"
            onClick={() => {
              setIsAddingTable(true);
              setEditingTable({
                name: '',
                description: '',
                columns: []
              });
              setSelectedTable(null);
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-2">
          {schema?.tables.map((table) => (
            <Card
              key={table.name}
              className={`cursor-pointer transition-colors ${
                selectedTable === table.name ? 'border-primary' : ''
              }`}
              onClick={() => {
                setSelectedTable(table.name);
                setEditingTable(null);
                setIsAddingTable(false);
              }}
            >
              <CardHeader className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TableIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{table.name}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {table.columns.length} columns
                </p>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        {message && (
          <Alert className="mb-6" variant={message.type === 'error' ? 'destructive' : 'default'}>
            {message.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        {(isAddingTable || editingTable) && editingTable ? (
          <div>
            <h2 className="text-2xl font-bold mb-6">
              {isAddingTable ? 'Create New Table' : 'Edit Table'}
            </h2>

            <Card>
              <CardContent className="pt-6">
                <div className="grid gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Table Name</label>
                    <Input
                      value={editingTable.name}
                      onChange={(e) => setEditingTable({
                        ...editingTable,
                        name: e.target.value
                      })}
                      placeholder="e.g., customers"
                      disabled={!isAddingTable}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <Textarea
                      value={editingTable.description}
                      onChange={(e) => setEditingTable({
                        ...editingTable,
                        description: e.target.value
                      })}
                      placeholder="Describe what this table stores..."
                      rows={3}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Columns</h3>
                      <Button
                        size="sm"
                        onClick={() => {
                          setIsAddingColumn(true);
                          setEditingColumn({
                            name: '',
                            type: 'varchar',
                            description: '',
                            nullable: true,
                            isForeignKey: false
                          });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Column
                      </Button>
                    </div>

                    {editingTable.columns.length === 0 && !isAddingColumn && (
                      <p className="text-muted-foreground text-center py-8">
                        No columns yet. Add a column to get started.
                      </p>
                    )}

                    <div className="space-y-2">
                      {editingTable.columns.map((column, index) => (
                        <Card key={index}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium">{column.name}</span>
                                  <span className="text-xs text-muted-foreground">({column.type})</span>
                                  {column.isPrimaryKey && (
                                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">PK</span>
                                  )}
                                  {column.isForeignKey && (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">FK</span>
                                  )}
                                  {column.hideInDisplay && (
                                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">Hidden</span>
                                  )}
                                  {column.isCurrencyColumn && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">$</span>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">{column.description}</p>
                                {column.displayAlias && (
                                  <p className="text-xs text-muted-foreground">Display: {column.displayAlias}</p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingColumn(column);
                                    setEditingColumnIndex(index);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRemoveColumn(index)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}

                      {(isAddingColumn || editingColumnIndex !== null) && editingColumn && (
                        <Card className="border-primary">
                          <CardContent className="p-4">
                            <div className="grid gap-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium mb-1">Column Name</label>
                                  <Input
                                    value={editingColumn.name}
                                    onChange={(e) => setEditingColumn({
                                      ...editingColumn,
                                      name: e.target.value
                                    })}
                                    placeholder="column_name"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium mb-1">Data Type</label>
                                  <Select
                                    value={editingColumn.type}
                                    onValueChange={(value) => setEditingColumn({
                                      ...editingColumn,
                                      type: value
                                    })}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {dataTypes.map(type => (
                                        <SelectItem key={type} value={type}>
                                          {type}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium mb-1">Description</label>
                                <Input
                                  value={editingColumn.description}
                                  onChange={(e) => setEditingColumn({
                                    ...editingColumn,
                                    description: e.target.value
                                  })}
                                  placeholder="Describe this column..."
                                />
                              </div>

                              <div className="border-t pt-3">
                                <h4 className="text-sm font-medium mb-3">Display Settings</h4>
                                
                                <div className="grid gap-3">
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-sm font-medium mb-1">Display Name</label>
                                      <Input
                                        value={editingColumn.displayAlias || ''}
                                        onChange={(e) => setEditingColumn({
                                          ...editingColumn,
                                          displayAlias: e.target.value || undefined
                                        })}
                                        placeholder={editingColumn.name?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium mb-1">Display Order</label>
                                      <Input
                                        type="number"
                                        value={editingColumn.displayOrder || ''}
                                        onChange={(e) => setEditingColumn({
                                          ...editingColumn,
                                          displayOrder: e.target.value ? parseInt(e.target.value) : undefined
                                        })}
                                        placeholder="Auto"
                                      />
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={editingColumn.hideInDisplay || false}
                                        onChange={(e) => setEditingColumn({
                                          ...editingColumn,
                                          hideInDisplay: e.target.checked
                                        })}
                                      />
                                      <span className="text-sm">Hide in Query Results</span>
                                    </label>

                                    <label className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={editingColumn.isCurrencyColumn || false}
                                        disabled={editingColumn.type !== 'decimal' && editingColumn.type !== 'int' && editingColumn.type !== 'bigint'}
                                        onChange={(e) => setEditingColumn({
                                          ...editingColumn,
                                          isCurrencyColumn: e.target.checked
                                        })}
                                      />
                                      <span className="text-sm">Currency Column</span>
                                    </label>
                                  </div>
                                </div>
                              </div>

                              <div className="border-t pt-3">
                                <div className="flex items-center gap-4 mb-3">
                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={editingColumn.isPrimaryKey || false}
                                      onChange={(e) => setEditingColumn({
                                        ...editingColumn,
                                        isPrimaryKey: e.target.checked
                                      })}
                                    />
                                    <span className="text-sm">Primary Key</span>
                                  </label>

                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={editingColumn.nullable || false}
                                      onChange={(e) => setEditingColumn({
                                        ...editingColumn,
                                        nullable: e.target.checked
                                      })}
                                    />
                                    <span className="text-sm">Nullable</span>
                                  </label>

                                  <label className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={editingColumn.isForeignKey || false}
                                      onChange={(e) => setEditingColumn({
                                        ...editingColumn,
                                        isForeignKey: e.target.checked,
                                        foreignKeyReference: e.target.checked ? editingColumn.foreignKeyReference || { table: '', column: '' } : undefined
                                      })}
                                    />
                                    <span className="text-sm">Foreign Key</span>
                                  </label>
                                </div>

                                {editingColumn.isForeignKey && (
                                  <div className="grid grid-cols-2 gap-3 mt-3 p-3 bg-muted rounded">
                                    <div>
                                      <label className="block text-sm font-medium mb-1">Reference Table</label>
                                      <Select
                                        value={editingColumn.foreignKeyReference?.table || 'none'}
                                        onValueChange={(value) => setEditingColumn({
                                          ...editingColumn,
                                          foreignKeyReference: {
                                            ...editingColumn.foreignKeyReference!,
                                            table: value === 'none' ? '' : value,
                                            column: ''
                                          }
                                        })}
                                      >
                                        <SelectTrigger className="h-8">
                                          <SelectValue placeholder="Select table" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="none">Select table...</SelectItem>
                                          {schema?.tables
                                            .filter(t => t.name !== editingTable?.name)
                                            .map(table => (
                                              <SelectItem key={table.name} value={table.name}>
                                                {table.name}
                                              </SelectItem>
                                            ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium mb-1">Reference Column</label>
                                      <Select
                                        value={editingColumn.foreignKeyReference?.column || 'none'}
                                        onValueChange={(value) => setEditingColumn({
                                          ...editingColumn,
                                          foreignKeyReference: {
                                            ...editingColumn.foreignKeyReference!,
                                            column: value === 'none' ? '' : value
                                          }
                                        })}
                                        disabled={!editingColumn.foreignKeyReference?.table}
                                      >
                                        <SelectTrigger className="h-8">
                                          <SelectValue placeholder="Select column" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="none">Select column...</SelectItem>
                                          {editingColumn.foreignKeyReference?.table && 
                                            schema?.tables
                                              .find(t => t.name === editingColumn.foreignKeyReference?.table)
                                              ?.columns
                                              .filter(col => col.isPrimaryKey)
                                              .map(col => (
                                                <SelectItem key={col.name} value={col.name}>
                                                  {col.name}
                                                </SelectItem>
                                              ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingColumn(null);
                                    setEditingColumnIndex(null);
                                    setIsAddingColumn(false);
                                  }}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={isAddingColumn ? handleAddColumn : handleUpdateColumn}
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  {isAddingColumn ? 'Add' : 'Update'}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>

                  {/* Default Sort Order */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium mb-2">Default Sort Order</label>
                    <Select
                      value={editingTable.defaultOrderBy || 'none'}
                      onValueChange={(value) => setEditingTable({
                        ...editingTable,
                        defaultOrderBy: value === 'none' ? undefined : value
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select default sort column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {editingTable.columns.map(col => (
                          <React.Fragment key={col.name}>
                            <SelectItem value={col.name}>{col.displayAlias || col.name} (Ascending)</SelectItem>
                            <SelectItem value={`${col.name} DESC`}>{col.displayAlias || col.name} (Descending)</SelectItem>
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingTable(null);
                        setIsAddingTable(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={isAddingTable ? handleCreateTable : handleUpdateTable}
                      disabled={!editingTable.name || !editingTable.description || editingTable.columns.length === 0}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {isAddingTable ? 'Create Table' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : selectedTableData ? (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">{selectedTableData.name}</h2>
                <p className="text-muted-foreground">{selectedTableData.description}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setEditingTable(selectedTableData)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit Table
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDeleteTable(selectedTableData.name)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Table
                </Button>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Columns</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {selectedTableData.columns.map((column) => (
                    <Card key={column.name}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{column.name}</span>
                              <span className="text-xs text-muted-foreground">({column.type})</span>
                              {column.isPrimaryKey && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">PK</span>
                              )}
                              {column.isForeignKey && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">FK</span>
                              )}
                              {!column.nullable && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">NOT NULL</span>
                              )}
                              {column.hideInDisplay && (
                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                  <EyeOff className="h-3 w-3 inline mr-1" />
                                  Hidden
                                </span>
                              )}
                              {column.isCurrencyColumn && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                  <DollarSign className="h-3 w-3 inline mr-1" />
                                  Currency
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{column.description}</p>
                            {column.displayAlias && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Display as: {column.displayAlias}
                              </p>
                            )}
                            {column.displayOrder !== undefined && (
                              <p className="text-xs text-muted-foreground">
                                Display order: {column.displayOrder}
                              </p>
                            )}
                            {column.isForeignKey && column.foreignKeyReference && (
                              <p className="text-xs text-muted-foreground mt-1">
                                References: {column.foreignKeyReference.table}.{column.foreignKeyReference.column}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteColumn(selectedTableData.name, column.name)}
                            disabled={column.isPrimaryKey}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Table Settings */}
            {selectedTableData.defaultOrderBy && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Table Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Default Sort:</span>
                    <Badge variant="secondary">
                      <SortAsc className="h-3 w-3 mr-1" />
                      {selectedTableData.defaultOrderBy}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                Select a table from the sidebar or create a new one to get started.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchemaEditorPage;